import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { COMMISSION_TYPE_PERCENTAGE } from 'common/financeConstants';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_LEASE_SIGNED } from 'api/actionTypes';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';

import SrDarkModal from 'client/components/SrDarkModal';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getFormFields = (formikBag = { values: {} }, household) => [
  {
    id: 'leaseSignedDate',
    title: 'When did the renters sign the lease?',
    type: 'date_picker',
    properties: {
      placeholder: 'MM/DD/YYYY',
      dateFormat: 'MM/DD/YYYY',
      date: formikBag.values.leaseStartDate,
      beforeOrSameThanDateMessage: 'This date has to be same or before the lease start date.',
    },
    validations: ['required', 'beforeOrSameThanDate'],
  },
  {
    id: 'rentPrice',
    title: 'Rent Price ',
    type: 'currency',
    properties: {},
    validations: ['required'],
  },
  hideFieldIf(household?.leaseSentDate, {
    id: 'leaseSentDate',
    title: 'Lease Sent Date',
    type: 'date_picker',
    label: 'You can find the lease sent date by finding the lease in DocuSign',
    properties: {
      placeholder: 'MM/DD/YYYY',
      dateFormat: 'MM/DD/YYYY',
      date: formikBag.values.leaseSignedDate,
      beforeOrSameThanDateMessage: 'This date has to be the same or before the lease sign date.',
    },
    validations: ['required', 'beforeOrSameThanDate'],
  }),
  {
    id: 'leaseStartDate',
    title: 'Lease Start Date',
    type: 'date_picker',
    properties: {
      placeholder: 'MM/DD/YYYY',
      dateFormat: 'MM/DD/YYYY',
    },
    validations: ['required'],
  },
  {
    id: 'leaseTerm',
    title: 'Lease Term (months)',
    type: 'short_text',
    properties: {
      pattern: '[0-9]*',
      placeholder: '12',
      onlyAcceptNumbers: true,
    },
    validations: ['required'],
  },
  {
    id: 'leaseDocument',
    title: 'Lease Document',
    type: 'file_single',
    properties: {
      fileAccept: 'application/pdf',
      filePath: '/lease-documents/{uuid}/LeaseDocument',
    },
    validations: ['required'],
  },
  {
    id: 'hasBuyersAgent',
    title: 'Has external buyers agent?',
    validations: ['required'],
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  hideFieldIf(!formikBag.values.hasBuyersAgent, {
    id: 'buyersAgentName',
    title: 'Agent Name',
    type: 'short_text',
    validations: ['required'],
  }),
  hideFieldIf(!formikBag.values.hasBuyersAgent, {
    id: 'buyersAgentPhone',
    title: 'Agent Phone',
    type: 'phone',
    validations: ['required', 'phone'],
  }),
  hideFieldIf(!formikBag.values.hasBuyersAgent, {
    id: 'buyersAgentEmail',
    title: 'Agent Email',
    type: 'email',
    validations: ['required', 'email'],
  }),
];

@withApiState({
  householdMarkAsLeaseSignedApiState: {
    action: SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_LEASE_SIGNED,
  },
})
class SrHouseholdLeaseSignedModal extends Component {
  static propTypes = {
    householdId: PropTypes.number.isRequired,
    listingId: PropTypes.number.isRequired,
    rentPrice: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    household: PropTypes.object.isRequired,
  };

  _onSubmitForm = (formValues) => {
    if (!global.confirm('Are you sure the lease info is correct?')) {
      return;
    }

    if (!formValues.hasBuyersAgent) {
      formValues.buyersAgentName = null;
      formValues.buyersAgentPhone = null;
      formValues.buyersAgentEmail = null;
    }

    const { householdId, listingId, onClose, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_LEASE_SIGNED,
      payload: setNullEmptyValues({
        householdId: householdId,
        listingId: listingId,
        rentPrice: formValues.rentPrice,
        leaseSignedDate: formValues.leaseSignedDate,
        leaseSentDate: formValues.leaseSentDate,
        leaseStartDate: formValues.leaseStartDate,
        leaseTerm: formValues.leaseTerm,
        commissionType: COMMISSION_TYPE_PERCENTAGE,
        leaseDocument: formValues.leaseDocument,
        hasBuyersAgent: formValues.hasBuyersAgent,
        buyersAgentName: formValues.buyersAgentName,
        buyersAgentPhone: formValues.buyersAgentPhone,
        buyersAgentEmail: formValues.buyersAgentEmail,
      }),
      onSuccess: () => {
        onClose({
          householdId,
          listingId,
        });
      },
    });
  };

  render() {
    const {
      onClose,
      rentPrice,
      householdMarkAsLeaseSignedApiState,
      household,
      listingId,
    } = this.props;

    const listingApplication = household.listingApplications.find(({ listing }) => {
      return listing.id === listingId;
    });

    return (
      <SrDarkModal onClose={onClose}>
        <div className="SrHouseholdLeaseModal">
          <div onClick={preventDefaultPropagation} className="SrHouseholdModalContent">
            <Formik
              initialValues={createInitialValues({
                formFields: getFormFields(),
                defaultValues: {
                  rentPrice,
                  hasBuyersAgent: listingApplication?.hasBuyersAgent,
                  buyersAgentName: listingApplication?.buyersAgentName,
                  buyersAgentPhone: listingApplication?.buyersAgentPhone,
                  buyersAgentEmail: listingApplication?.buyersAgentEmail,
                },
              })}
              validate={(values) => {
                const formFields = getFormFields({ values }, household);
                return createFormValidationFn(formFields)(values);
              }}
              onSubmit={this._onSubmitForm}
              render={(formikBag) => {
                const formFields = getFormFields(formikBag, household);
                return (
                  <Form>
                    <h1 className="SrHouseholdModalTitle">send to lease sign sheet</h1>

                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                    ))}
                    <ButtonDeprecated
                      type="submit"
                      onClick={formikBag.handleSubmit}
                      disabled={!formikBag.isValid}
                    >
                      {householdMarkAsLeaseSignedApiState.inProgress
                        ? 'please wait...'
                        : 'send to lease sign sheet'}
                    </ButtonDeprecated>
                  </Form>
                );
              }}
            />

            {householdMarkAsLeaseSignedApiState.error && (
              <SrBox marginTop="10px" borderRadius="3px" overflow="hidden">
                <SrErrorMessage>{householdMarkAsLeaseSignedApiState.error.message}</SrErrorMessage>
              </SrBox>
            )}
          </div>
        </div>
      </SrDarkModal>
    );
  }
}

SrHouseholdLeaseSignedModal.displayName = 'SrHouseholdLeaseSignedModal';

export default SrHouseholdLeaseSignedModal;
